import { userApiClient, ApiError } from '../api/client';
import type { User } from '../../types';

/**
 * Mirror of the backend `SwitchOrganizationResponse` (defined in
 * `secure_login.py`). Same shape as the login response so the frontend can
 * update auth state in-place after a switch without a follow-up /auth/me.
 */
export interface SwitchOrganizationResponse {
  user: {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    roles?: string[];
    permissions?: string[];
  };
  organization: {
    id: string;
    name: string;
    slug?: string | null;
  };
  expiresIn: number;
  subscriptionStatus?: string | null;
}

export type SwitchedUser = User;

export interface UserOrganization {
  id: string;
  name: string;
  slug: string | null;
  displayName: string | null;
  // Opt-in only: present solely when the caller requested `includeRoles`. Holds
  // ALL of the user's role simple-names for the org (deduped). A pure display
  // artifact — never the basis for an access/capability decision.
  roles?: string[];
  // Per-org capability flag (always present on approved orgs): whether the
  // caller holds organizations:enable in this org. Drives the Re-enable control.
  canEnable?: boolean;
  status: 'approved' | 'pending';
  enabled?: boolean;
  joinCode?: string | null;
  joinCodeEnabled?: boolean;
  requestedAt?: string;
  memberCount?: number;
  tierName?: string | null;
  ownerContact?: { name: string | null; email: string } | null;
  isManagedByPartner?: boolean;
  partnerName?: string | null;
}

export interface UserOrganizationsResponse {
  approved: UserOrganization[];
  pending: UserOrganization[];
}

/**
 * One organization from `GET /api/v1/user/organizations`. Unlike the
 * approved/pending list, this surface flags which org is the user's pinned
 * default (`isDefault`) — the single source for the default-org preference UI.
 */
export interface DefaultOrgSummary {
  id: string;
  name: string;
  slug: string;
  isDefault: boolean;
  createdAt: string;
}

export interface UserOrganizationsWithDefaultResponse {
  organizations: DefaultOrgSummary[];
}

export interface SetDefaultOrganizationResponse {
  success: boolean;
  defaultOrganization: DefaultOrgSummary | null;
}

class OrganizationService {
  async getUserOrganizations(
    includePending: boolean = true,
    options: { includeRoles?: boolean } = {},
  ): Promise<UserOrganizationsResponse> {
    try {
      // Roles are opt-in: only requested when a consumer actually displays them
      // (see MembershipCard). The default call omits them so the response never
      // carries role data by default.
      const rolesParam = options.includeRoles ? '&include_roles=true' : '';
      const response = await userApiClient.get<UserOrganizationsResponse>(
        `/api/v1/users/me/organizations?include_pending=${includePending}${rolesParam}`,
        { skipAuth: false },
      );
      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get user organizations', 0, 'UnknownError', error);
    }
  }

  /**
   * Fetch the user's organizations with the pinned-default flag. Drives the
   * default-org preference control (memberships + current pin in one call).
   */
  async getOrganizationsWithDefault(): Promise<UserOrganizationsWithDefaultResponse> {
    try {
      return await userApiClient.get<UserOrganizationsWithDefaultResponse>(
        '/api/v1/user/organizations',
        { skipAuth: false },
      );
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to get organizations', 0, 'UnknownError', error);
    }
  }

  /**
   * Set (or clear) the user's pinned default organization.
   *
   * @param organizationId - org id to pin, or `null` to clear the pin
   *   ("Last actively used").
   */
  async setDefaultOrganization(
    organizationId: string | null,
  ): Promise<SetDefaultOrganizationResponse> {
    try {
      return await userApiClient.request<SetDefaultOrganizationResponse>(
        '/api/v1/user/default-organization',
        {
          method: 'PATCH',
          body: JSON.stringify({ organizationId }),
        },
      );
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to set default organization', 0, 'UnknownError', error);
    }
  }

  async switchOrganization(organizationId: string): Promise<SwitchOrganizationResponse> {
    try {
      return await userApiClient.post<SwitchOrganizationResponse>(
        '/api/v1/auth/secure/switch-organization',
        { organizationId },
      );
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to switch organization', 0, 'UnknownError', error);
    }
  }
}

export const organizationService = new OrganizationService();
